import { useEffect, useMemo, useRef, useState, type ElementType, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider,
  Grid, FormControlLabel, Switch, Alert, IconButton, InputAdornment,
  Autocomplete, Chip, LinearProgress, alpha,
} from '@mui/material';
import {
  Close, ArrowBack, Visibility, VisibilityOff, Person, DirectionsCar, DeliveryDining,
  Storefront, Handyman, Event, Badge, AdminPanelSettings,
} from '@mui/icons-material';
import GeoMasterSelect from '../masters/GeoMasterSelect';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import AddressLookupField from '../ui/forms/AddressLookupField';
import PartnerReviewDetail from '../documents/PartnerReviewDetail';
import type { PartnerRow } from '../../services/partnerUserService';
import { SERVICE_HUB_OPTIONS } from '../../constants/partnerServices';
import {
  USER_TYPE_FORM_OPTIONS,
  USER_TYPE_LABELS,
  USER_TYPE_ROLE_HINTS,
  GENDER_OPTIONS,
  PARTNER_USER_TYPES,
  STAFF_USER_TYPES,
  PARTNER_ENTITY_BY_USER_TYPE,
  PREFERRED_LANGUAGE_OPTIONS,
  getFormTabsForUserType,
  getUserTypeFormConfig,
  emptyUserForm,
  userRowToForm,
  validateUserForm,
  maxDateOfBirth,
  minAgeForUserType,
  resolveBlockedReason,
  type UserFormState,
  type UserRow,
} from '../../constants/userManagement';
import { getUserById, listAdminRoles } from '../../services/userService';
import { useI18n } from '../../i18n/I18nProvider';
import { listCountryDialCodes } from '../../services/countryDialCodeService';

interface ZoneOption { _id: string; name: string; code?: string }

/** Only languages active in the Languages master; a saved inactive one stays visible but can't be re-picked. */
function PreferredLanguageSelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const { langList } = useI18n();
  const savedInactive = value && !langList.some((l) => l.code === value)
    ? PREFERRED_LANGUAGE_OPTIONS.find((l) => l.code === value)
    : undefined;
  return (
    <TextField fullWidth size="small" select label="Preferred Language" value={value}
      onChange={(e) => onChange(e.target.value)}
      helperText={savedInactive ? `${savedInactive.name} is inactive — pick an active language` : 'Active languages from the Languages master'}>
      <MenuItem value="">Select language</MenuItem>
      {langList.map((lang) => (
        <MenuItem key={lang.code} value={lang.code}>
          {lang.label}{lang.nativeLabel !== lang.label ? ` · ${lang.nativeLabel}` : ''}
        </MenuItem>
      ))}
      {savedInactive && <MenuItem value={savedInactive.code} disabled>{savedInactive.name} (inactive)</MenuItem>}
    </TextField>
  );
}

const ROLE_ICONS: Record<string, ElementType> = {
  customer: Person,
  driver: DirectionsCar,
  delivery_partner: DeliveryDining,
  merchant: Storefront,
  service_provider: Handyman,
  vendor: Event,
  employee: Badge,
  client_admin: AdminPanelSettings,
};

const ROLE_SHORT_LABELS: Record<string, string> = {
  customer: 'Customer',
  driver: 'Driver',
  delivery_partner: 'Delivery',
  merchant: 'Merchant',
  service_provider: 'Service Pro',
  vendor: 'Vendor',
  employee: 'Employee',
  client_admin: 'Admin',
};

const ROLE_COLORS: Record<string, string> = {
  customer: '#64748b',
  driver: '#2563eb',
  delivery_partner: '#ea580c',
  merchant: '#059669',
  service_provider: '#7c3aed',
  vendor: '#db2777',
  employee: '#0891b2',
  client_admin: '#6366f1',
};

interface UserFormDrawerProps {
  open: boolean;
  onClose: () => void;
  editingUser: UserRow | null;
  zones: ZoneOption[];
  loading?: boolean;
  saveError?: string;
  onSave: (form: UserFormState) => void;
  defaultUserType?: string;
  defaultServiceHub?: string;
  lockServiceHub?: boolean;
  allowedUserTypes?: string[];
}

export default function UserFormDrawer({
  open,
  onClose,
  editingUser,
  zones,
  loading,
  saveError,
  onSave,
  defaultUserType,
  defaultServiceHub,
  lockServiceHub,
  allowedUserTypes,
}: UserFormDrawerProps) {
  const isEdit = !!editingUser;
  const singleAllowedType = allowedUserTypes?.length === 1 ? allowedUserTypes[0] : null;
  const [form, setForm] = useState<UserFormState>(() => emptyUserForm({
    userType: singleAllowedType || defaultUserType || 'customer',
  }));
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'type' | 'form'>(() => (
    (singleAllowedType || defaultUserType) ? 'form' : 'type'
  ));
  const bodyRef = useRef<HTMLDivElement>(null);

  const { data: fullUser, isLoading: loadingUser, isError: loadUserError } = useQuery({
    queryKey: ['admin-user', editingUser?._id],
    queryFn: () => getUserById(editingUser!._id),
    enabled: open && !!editingUser?._id,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['admin-roles-mini'],
    queryFn: listAdminRoles,
    enabled: open,
    staleTime: 60_000,
  });

  useQuery({
    queryKey: ['country-dial-codes'],
    queryFn: listCountryDialCodes,
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const userTypeOptions = useMemo(() => {
    if (!allowedUserTypes?.length) return USER_TYPE_FORM_OPTIONS;
    return USER_TYPE_FORM_OPTIONS.filter((o) => allowedUserTypes.includes(o.value));
  }, [allowedUserTypes]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setShowPassword(false);
    const singleType = allowedUserTypes?.length === 1 ? allowedUserTypes[0] : null;
    if (editingUser) {
      setForm(userRowToForm(fullUser || editingUser));
      setStep('form');
    } else {
      const userType = singleType || defaultUserType || '';
      const cfg = getUserTypeFormConfig(userType || 'customer');
      setForm(emptyUserForm({
        userType,
        serviceHub: defaultServiceHub || cfg.defaultServiceHub || '',
        partnerEntityType: PARTNER_ENTITY_BY_USER_TYPE[userType] || '',
      }));
      setStep(userType ? 'form' : 'type');
    }
  }, [open, editingUser, fullUser, defaultUserType, defaultServiceHub, allowedUserTypes, userTypeOptions]);

  const tabs = useMemo(
    () => getFormTabsForUserType(form.userType),
    [form.userType],
  );
  const typeConfig = getUserTypeFormConfig(form.userType);
  const canChangeRole = !isEdit && userTypeOptions.length > 1;
  const showTypePicker = !isEdit && step === 'type';
  const showForm = isEdit || step === 'form';

  const patch = (p: Partial<UserFormState>) => setForm((f) => ({ ...f, ...p }));

  const onUserTypeChange = (userType: string) => {
    const cfg = getUserTypeFormConfig(userType);
    patch({
      userType,
      partnerEntityType: PARTNER_ENTITY_BY_USER_TYPE[userType] || '',
      serviceHub: lockServiceHub
        ? (defaultServiceHub || form.serviceHub)
        : (cfg.defaultServiceHub || form.serviceHub),
      roleIds: STAFF_USER_TYPES.has(userType) ? form.roleIds : [],
      zoneId: cfg.requiresZone ? form.zoneId : '',
      zoneIds: STAFF_USER_TYPES.has(userType) ? form.zoneIds : [],
    });
    setErrors({});
  };

  const pickUserType = (userType: string) => {
    onUserTypeChange(userType);
    setStep('form');
  };

  const goBackToTypes = () => {
    if (!canChangeRole) {
      onClose();
      return;
    }
    setErrors({});
    setStep('type');
  };

  const isPartner = PARTNER_USER_TYPES.has(form.userType);
  const isStaff = STAFF_USER_TYPES.has(form.userType);
  const selectedStaffZones = useMemo(
    () => zones.filter((z) => (form.zoneIds || []).includes(z._id)),
    [zones, form.zoneIds],
  );
  const selectedRoles = useMemo(
    () => roles.filter((r) => (form.roleIds || []).includes(r._id)),
    [roles, form.roleIds],
  );

  const saveForm = () => {
    const finalErrors = validateUserForm(form, isEdit);
    if (Object.keys(finalErrors).length) {
      setErrors(finalErrors);
      requestAnimationFrame(() => {
        const invalid = bodyRef.current?.querySelector('[aria-invalid="true"]') as HTMLElement | null;
        invalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    onSave(form);
  };

  const renderProfileTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <PersonNameField fullWidth size="small" required label="First Name" value={form.firstName}
          error={!!errors.firstName} helperText={errors.firstName || 'Letters only — no numbers or special characters'}
          onChange={(e) => patch({ firstName: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PersonNameField fullWidth size="small" label="Last Name" value={form.lastName}
          error={!!errors.lastName} helperText={errors.lastName || 'Letters only — no numbers or special characters'}
          onChange={(e) => patch({ lastName: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" type="email" required={!form.phone.trim()} label="Email" value={form.email}
          error={!!errors.email || !!errors.contact} helperText={errors.email || errors.contact}
          onChange={(e) => patch({ email: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <PhoneWithDialCodeField
          label="Phone Number"
          phone={form.phone}
          countryCode={form.phoneCountryCode}
          preferredCountry={form.country}
          error={!!errors.phone}
          helperText={errors.phone}
          required={PARTNER_USER_TYPES.has(form.userType)}
          onChange={({ phone, countryCode }) => patch({ phone, phoneCountryCode: countryCode })}
        />
      </Grid>
      {errors.contact && (
        <Grid item xs={12}>
          <Alert severity="error" variant="outlined">{errors.contact}</Alert>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Gender" value={form.gender}
          onChange={(e) => patch({ gender: e.target.value })}>
          {GENDER_OPTIONS.map((g) => (
            <MenuItem key={g.value || 'na'} value={g.value}>{g.label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Date of Birth"
          required={PARTNER_USER_TYPES.has(form.userType)}
          value={form.dateOfBirth}
          error={!!errors.dateOfBirth}
          helperText={errors.dateOfBirth || `Must be at least ${minAgeForUserType(form.userType)} years old`}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxDateOfBirth(form.userType) }}
          onChange={(e) => patch({ dateOfBirth: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <GeoMasterSelect masterType="countries" label="Country" value={form.country}
          onChange={(code) => patch({ country: code })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PreferredLanguageSelect value={form.language} onChange={(language) => patch({ language })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <PhoneWithDialCodeField
          label="Emergency Contact"
          phone={form.emergencyPhone}
          countryCode={form.emergencyPhoneCountryCode}
          preferredCountry={form.country}
          error={!!errors.emergencyPhone}
          helperText={errors.emergencyPhone}
          onChange={({ phone, countryCode }) => patch({ emergencyPhone: phone, emergencyPhoneCountryCode: countryCode })}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormFileUpload label="Profile Photo" value={form.avatar}
          onChange={(url) => patch({ avatar: url })} folder="users/avatars" />
      </Grid>
    </Grid>
  );

  const renderRoleSelectStep = () => {
    const groups = [...new Set(userTypeOptions.map((o) => o.group))];
    const selectedHint = form.userType ? USER_TYPE_ROLE_HINTS[form.userType] : null;
    const selectedColor = form.userType ? (ROLE_COLORS[form.userType] || '#64748b') : undefined;

    return (
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          Choose the form to use. A details drawer opens next with only the fields for that account type. Use Back to change type.
        </Typography>

        {groups.map((group, groupIndex) => (
          <Box key={group}>
            {groupIndex > 0 && <Divider sx={{ mb: 1.5 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: 'block', fontWeight: 700, letterSpacing: '0.04em' }}
            >
              {group.toUpperCase()}
            </Typography>
            <Grid container spacing={1}>
              {userTypeOptions.filter((o) => o.group === group).map((opt) => {
                const Icon = ROLE_ICONS[opt.value] || Person;
                const color = ROLE_COLORS[opt.value] || '#64748b';
                const selected = form.userType === opt.value;
                return (
                  <Grid item xs={6} key={opt.value}>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => pickUserType(opt.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          pickUserType(opt.value);
                        }
                      }}
                      sx={{
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: selected ? color : 'divider',
                        bgcolor: selected ? alpha(color, 0.07) : 'background.paper',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minHeight: 48,
                        boxShadow: selected ? `0 0 0 1px ${alpha(color, 0.25)}` : 'none',
                        transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          borderColor: color,
                          bgcolor: alpha(color, 0.04),
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${alpha(color, 0.45)}`,
                          outlineOffset: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(color, 0.1),
                          color,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 17 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={650} noWrap sx={{ fontSize: '0.8125rem' }}>
                        {ROLE_SHORT_LABELS[opt.value] || opt.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        {selectedHint && selectedColor && (
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(selectedColor, 0.22),
              bgcolor: alpha(selectedColor, 0.05),
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              {selectedHint.summary}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: selectedColor, fontWeight: 650 }}>
              {selectedHint.steps}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  const renderSelectedRoleBanner = () => {
    if (!form.userType) return null;
    const color = ROLE_COLORS[form.userType] || '#64748b';
    const Icon = ROLE_ICONS[form.userType] || Person;
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 2,
          px: 1.25,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(color, 0.2),
          bgcolor: alpha(color, 0.04),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.12),
              color,
            }}
          >
            <Icon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="body2" fontWeight={650} noWrap>
            {USER_TYPE_LABELS[form.userType] || form.userType}
          </Typography>
        </Stack>
        {canChangeRole && (
          <Button size="small" onClick={goBackToTypes}>Change type</Button>
        )}
      </Stack>
    );
  };

  const renderAssignmentTab = () => (
    <Grid container spacing={2}>
      {isStaff && (
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={zones}
            value={selectedStaffZones}
            getOptionLabel={(z) => `${z.name}${z.code ? ` (${z.code})` : ''}`}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            onChange={(_, value) => patch({
              zoneIds: value.map((z) => z._id),
              zoneId: value[0]?._id || '',
            })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option._id} label={option.code || option.name} size="small" />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Operating zones"
                helperText="Assign one or more zones for city/zone admin staff. They manage partners, drivers, and ops only in these zones."
              />
            )}
          />
        </Grid>
      )}
      {(isPartner || typeConfig.requiresServiceHub) && (
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Service Hub" value={form.serviceHub}
          disabled={lockServiceHub}
          error={!!errors.serviceHub} helperText={errors.serviceHub || (typeConfig.requiresServiceHub ? 'Required for partners' : '')}
          onChange={(e) => patch({ serviceHub: e.target.value })}>
          <MenuItem value="">None</MenuItem>
          {SERVICE_HUB_OPTIONS.map((h) => (
            <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
          ))}
        </TextField>
      </Grid>
      )}
      {(isPartner || typeConfig.requiresZone) && (
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" select label="Operating Zone" value={form.zoneId}
          error={!!errors.zoneId} helperText={errors.zoneId || (typeConfig.requiresZone ? 'Required for partners' : '')}
          onChange={(e) => patch({ zoneId: e.target.value, zoneIds: e.target.value ? [e.target.value] : [] })}>
          <MenuItem value="">No zone</MenuItem>
          {zones.map((z) => (
            <MenuItem key={z._id} value={z._id}>{z.name}{z.code ? ` (${z.code})` : ''}</MenuItem>
          ))}
        </TextField>
      </Grid>
      )}
      <Grid item xs={12}>
        <Alert severity="info" variant="outlined">
          {isStaff
            ? 'Tenant admins see all zones. City admins with assigned zones manage partners, drivers, dispatch, and onboarding only in those zones.'
            : 'Partners need a zone and hub before they can receive jobs.'}
        </Alert>
      </Grid>
    </Grid>
  );

  const renderPartnerTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" required={['merchant', 'vendor', 'service_provider'].includes(form.userType)}
          label="Business / Store Name" value={form.businessName}
          error={!!errors.businessName} helperText={errors.businessName}
          onChange={(e) => patch({ businessName: e.target.value })} />
      </Grid>
      {(form.userType === 'driver' || form.userType === 'delivery_partner') && (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Vehicle Number" value={form.vehicleNumber}
            onChange={(e) => patch({ vehicleNumber: e.target.value })} />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Referral Code" value={form.referralCode}
          onChange={(e) => patch({ referralCode: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" type="number" label="Commission %" value={form.commissionPercent}
          inputProps={{ min: 0, max: 100, step: 0.5 }}
          onChange={(e) => patch({ commissionPercent: e.target.value })} />
      </Grid>
    </Grid>
  );

  const renderDocumentsTab = () => {
    const entityType = form.partnerEntityType || PARTNER_ENTITY_BY_USER_TYPE[form.userType] || 'driver';
    const source = fullUser || editingUser;
    if (!isEdit || !source?._id) {
      return (
        <Alert severity="info">
          Create this driver first, then open Edit User → Documents to upload, approve, or reject KYC.
          Approved documents are required before they can use the driver app.
        </Alert>
      );
    }
    const zoneRef = source.zoneId;
    const zoneId = form.zoneId || (typeof zoneRef === 'object' ? zoneRef?._id : zoneRef);
    const partner: PartnerRow = {
      _id: source._id,
      firstName: form.firstName || source.firstName || '',
      lastName: form.lastName || source.lastName,
      email: form.email || source.email,
      phone: form.phone || source.phone,
      userType: form.userType || source.userType || 'driver',
      partnerEntityType: entityType,
      isActive: source.isActive !== false,
      isVerified: !!source.isVerified,
      isBlocked: !!source.isBlocked,
      partnerStatus: source.partnerStatus || 'pending_approval',
      zoneId: typeof zoneRef === 'object' && zoneRef
        ? zoneRef
        : zoneId
          ? { _id: zoneId, name: '' }
          : undefined,
      serviceHub: form.serviceHub || source.serviceHub,
    };
    return (
      <Stack spacing={1.5}>
        <Alert severity="info" variant="outlined">
          Approve required documents here. The driver can use the driver app only after verification is complete.
        </Alert>
        <PartnerReviewDetail partner={partner} entityType={entityType} embedded />
      </Stack>
    );
  };

  const renderAccessTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={roles}
          value={selectedRoles}
          getOptionLabel={(opt) => opt.name || opt.code || opt._id}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          onChange={(_, value) => patch({ roleIds: value.map((r) => r._id) })}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option._id} label={option.name} size="small" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Assigned Roles"
              required={isStaff}
              error={!!errors.roleIds}
              placeholder="Select roles for dashboard access…"
              helperText={errors.roleIds || (typeConfig.suggestRoles ? 'Required for staff and admin accounts' : '')} />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Employee ID" value={form.employeeId}
          onChange={(e) => patch({ employeeId: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Department" value={form.department}
          placeholder="e.g. Operations, Dispatch"
          onChange={(e) => patch({ department: e.target.value })} />
      </Grid>
    </Grid>
  );

  const renderSecurityTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth size="small"
          type={showPassword ? 'text' : 'password'}
          label={isEdit ? 'New Password (optional)' : 'Password'}
          value={form.password}
          error={!!errors.password}
          helperText={errors.password || (isEdit ? 'Leave blank to keep current' : 'Min 6 characters')}
          placeholder={isEdit ? '' : 'Welcome@2024'}
          onChange={(e) => patch({ password: e.target.value })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth size="small"
          type={showPassword ? 'text' : 'password'}
          label="Confirm Password"
          value={form.confirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          onChange={(e) => patch({ confirmPassword: e.target.value })}
        />
      </Grid>
      {!isEdit && isPartner && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={form.autoVerify} onChange={(e) => patch({ autoVerify: e.target.checked })} />}
            label="Auto-verify partner (skip document approval queue)"
          />
          <Typography variant="caption" display="block" color={errors.autoVerify ? 'error' : 'text.secondary'}>
            {errors.autoVerify || 'Refused while Settings → Partner Onboarding → Document Approval Required is ON and this partner type has mandatory documents. Otherwise the partner stays inactive until approved.'}
          </Typography>
        </Grid>
      )}
      {!isEdit && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={form.sendWelcomeEmail} onChange={(e) => patch({ sendWelcomeEmail: e.target.checked })} />}
            label="Send welcome notification (when configured)"
          />
        </Grid>
      )}
    </Grid>
  );

  const renderNotesTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <AddressLookupField
          label="Street Address"
          value={form.address}
          country={form.country}
          onAddressChange={(address) => patch({ address })}
          onSelect={(loc) => {
            patch({
              address: loc.address || form.address,
              city: loc.city || form.city,
              state: loc.state || form.state,
              postalCode: loc.postalCode || form.postalCode,
              country: loc.countryCode || form.country,
            });
          }}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="City" value={form.city}
          onChange={(e) => patch({ city: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="State / Region" value={form.state}
          onChange={(e) => patch({ state: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Postal / PIN Code" value={form.postalCode}
          onChange={(e) => patch({ postalCode: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" multiline minRows={3} label="Internal Admin Notes"
          value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
      </Grid>
    </Grid>
  );

  const section = (title: string, body: ReactNode) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>{title}</Typography>
      {body}
    </Box>
  );

  if (!open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        root: { sx: { zIndex: (t) => t.zIndex.modal + 4 } },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, md: tabs.includes('documents') ? 880 : 640 },
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          {showForm && canChangeRole && (
            <IconButton size="small" onClick={goBackToTypes} sx={{ mt: 0.25 }} aria-label="Back">
              <ArrowBack />
            </IconButton>
          )}
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={800}>
              {isEdit
                ? 'Edit User'
                : showTypePicker
                  ? 'Choose account type'
                  : `Add ${USER_TYPE_LABELS[form.userType] || 'User'}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? `${form.firstName} ${form.lastName}`.trim() || editingUser?.email || 'Update account'
                : showTypePicker
                  ? 'Pick a form, then fill only the fields for that role'
                  : USER_TYPE_ROLE_HINTS[form.userType]?.summary || 'Complete the required fields to create this account'}
            </Typography>
            {!isEdit && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {showTypePicker ? 'Step 1 of 2 · Account type' : 'Step 2 of 2 · Details'}
              </Typography>
            )}
            {isEdit && (fullUser?.createdAt || editingUser?.createdAt) && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Registered {new Date(String(fullUser?.createdAt || editingUser?.createdAt)).toLocaleString()}
              </Typography>
            )}
            {isEdit && (fullUser?.isBlocked || editingUser?.isBlocked) && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
                Blocked — {resolveBlockedReason(fullUser || editingUser || { isBlocked: true })}
              </Alert>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }} aria-label="Close">
            <Close />
          </IconButton>
        </Stack>

        {(loadingUser && isEdit) && <LinearProgress />}

        <Box ref={bodyRef} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loadUserError && isEdit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not refresh full profile from server — showing list data. You can still edit and save.
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>{saveError}</Alert>
          )}
          {showTypePicker && (
            <>
              {errors.userType && (
                <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>{errors.userType}</Alert>
              )}
              {renderRoleSelectStep()}
            </>
          )}
          {showForm && (
            <>
              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
                  Please fix the highlighted required fields.
                </Alert>
              )}
              {renderSelectedRoleBanner()}
              {section('Contact & identity', renderProfileTab())}
              {tabs.includes('assignment') && section('Zone & hub', renderAssignmentTab())}
              {tabs.includes('partner') && section('Partner details', renderPartnerTab())}
              {tabs.includes('documents') && section('Documents', renderDocumentsTab())}
              {tabs.includes('access') && section('Access & roles', renderAccessTab())}
              {tabs.includes('security') && section('Login & security', renderSecurityTab())}
              {tabs.includes('notes') && section('Address & notes', renderNotesTab())}
            </>
          )}
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
          {showForm && canChangeRole ? (
            <Button startIcon={<ArrowBack />} onClick={goBackToTypes}>Back</Button>
          ) : (
            <Button onClick={onClose}>Cancel</Button>
          )}
          {showTypePicker ? (
            <Button
              variant="contained"
              disabled={!form.userType}
              onClick={() => {
                if (!form.userType) {
                  setErrors({ userType: 'Select an account type' });
                  return;
                }
                setStep('form');
              }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled={loading || loadingUser}
              onClick={saveForm}
            >
              {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
